import React, { useCallback, useEffect, useState } from 'react';
import { FaDownload, FaFilter, FaSyncAlt } from 'react-icons/fa';
import useApi from '../../hooks/useApi';
import api from '../../api';
import Alert from '../common/Alert';

export default function VehicleDocumentVerification() {
  const { get, put } = useApi();
  const [items, setItems] = useState([]);
  const [division, setDivision] = useState('');
  const [houseNo, setHouseNo] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (filters = {}) => {
    setLoading(true);
    try {
      const params = {};
      if (filters.division?.trim()) params.division = filters.division.trim();
      if (filters.houseNo?.trim()) params.houseNo = filters.houseNo.trim();
      setItems(await get('/api/vehicle-documents', { params }));
      setError('');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => { load(); }, [load]);

  const decide = async (id, status) => {
    const accepted = status === 'VERIFIED';
    const comment = window.prompt(
      `${accepted ? 'Verification' : 'Rejection'} comment`,
      accepted ? 'RC details verified.' : 'Please upload a valid RC document.'
    );
    if (comment === null) return;
    try {
      await put(`/api/vehicle-documents/${id}/verify`, { status, comment });
      await load({ division, houseNo });
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const download = async (document) => {
    try {
      const response = await api.get(`/api/vehicle-documents/${document.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const anchor = window.document.createElement('a');
      anchor.href = url;
      anchor.download = document.originalName;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message);
    }
  };

  const clearFilters = () => {
    setDivision('');
    setHouseNo('');
    load();
  };

  return (
    <div className="engagement-page">
      <div className="engagement-heading">
        <div>
          <span>Vehicle compliance</span>
          <h2>RC document verification</h2>
          <p>Review optional vehicle documents submitted by residents.</p>
        </div>
        <button className="btn btn-outline-primary" onClick={() => load({ division, houseNo })} disabled={loading}>
          <FaSyncAlt className="me-2" /> Refresh
        </button>
      </div>

      {error && <Alert type="danger" message={error} onClose={() => setError('')} />}

      <section className="engagement-list mb-3">
        <form className="row g-3 align-items-end p-3" onSubmit={(event) => {
          event.preventDefault();
          load({ division, houseNo });
        }}>
          <div className="col-sm-4 col-lg-3">
            <label className="form-label" htmlFor="rc-division">Division</label>
            <input id="rc-division" className="form-control" value={division} onChange={(e) => setDivision(e.target.value)} placeholder="e.g. A" />
          </div>
          <div className="col-sm-4 col-lg-3">
            <label className="form-label" htmlFor="rc-house">House number</label>
            <input id="rc-house" className="form-control" value={houseNo} onChange={(e) => setHouseNo(e.target.value)} placeholder="e.g. 101" />
          </div>
          <div className="col-sm-4 col-lg-4 d-flex gap-2">
            <button className="btn btn-primary" type="submit" disabled={loading}><FaFilter className="me-1" /> Apply filters</button>
            <button className="btn btn-outline-secondary" type="button" onClick={clearFilters} disabled={loading}>Clear</button>
          </div>
          <div className="col-lg-2 text-lg-end text-muted small">{items.length} result{items.length === 1 ? '' : 's'}</div>
        </form>
      </section>

      <section className="engagement-list">
        <div className="table-responsive">
          <table className="table align-middle">
            <thead><tr><th>Resident</th><th>Location</th><th>Vehicle</th><th>Document</th><th>Uploaded</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {items.map((document) => (
                <tr key={document.id}>
                  <td><strong>{document.ownerName}</strong></td>
                  <td>{[document.division, document.houseNo].filter(Boolean).join(' - ') || 'Not provided'}</td>
                  <td>{document.vehicleNo}</td>
                  <td><button className="btn btn-link p-0 text-start" onClick={() => download(document)}><FaDownload className="me-1" />{document.originalName}</button></td>
                  <td><small>{new Date(document.createdAt).toLocaleString()}<br />by {document.uploadedByName}</small></td>
                  <td>
                    <span className={`badge ${document.verificationStatus === 'VERIFIED' ? 'bg-success' : document.verificationStatus === 'REJECTED' ? 'bg-danger' : 'bg-warning text-dark'}`}>{document.verificationStatus}</span>
                    {document.verificationComment && <small className="d-block text-muted mt-1">{document.verificationComment}</small>}
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <button className="btn btn-sm btn-outline-success" disabled={document.verificationStatus === 'VERIFIED'} onClick={() => decide(document.id, 'VERIFIED')}>Verify</button>
                      <button className="btn btn-sm btn-outline-danger" disabled={document.verificationStatus === 'REJECTED'} onClick={() => decide(document.id, 'REJECTED')}>Reject</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && items.length === 0 && <div className="engagement-empty">No RC documents match the selected location.</div>}
        {loading && <div className="engagement-empty">Loading vehicle documents...</div>}
      </section>
    </div>
  );
}
